import React, { useState, useEffect } from 'react';
import { apiFetch } from '../services/api_service';
import { API } from '../App';
import { Target, TrendingUp, DollarSign, AlertCircle, CheckCircle2, Info } from 'lucide-react';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, ZAxis } from 'recharts';

const DoubleMateriality = () => {
  const [materialityData, setMaterialityData] = useState([]);
  const [indicators, setIndicators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');

  const fetchData = async () => {
    const [matData, indData] = await Promise.all([
      apiFetch(`${API}/materiality`),
      apiFetch(`${API}/indicators`)
    ]);
    if (matData?._error || indData?._error) {
      console.error('Error fetching data:', matData?._error ? matData.message : indData.message);
      setLoading(false);
      return;
    }
    setMaterialityData(matData);
    setIndicators(indData);
    setLoading(false);
  };

   
  useEffect(() => {
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Prepare data for scatter plot
  const scatterData = materialityData.map(item => ({
    x: item.impact_score,
    y: item.financial_score,
    name: item.indicator_name,
    category: item.category,
    classification: item.double_materiality_classification,
    size: 400
  }));

  // Filter by category
  const filteredData = selectedCategory === 'all' 
    ? scatterData 
    : scatterData.filter(item => item.category === selectedCategory);

  // Get unique categories
  const categories = ['all', ...new Set(materialityData.map(m => m.category))];

  // Classification color mapping
  const getClassificationColor = (classification) => {
    switch (classification) {
      case 'Double Material': return '#ef4444';
      case 'Impact Material': return '#f59e0b';
      case 'Financial Material': return '#3b82f6';
      case 'Not Material': return '#94a3b8';
      default: return '#64748b';
    }
  };

  // Count by classification
  const classificationCounts = materialityData.reduce((acc, item) => {
    acc[item.double_materiality_classification] = (acc[item.double_materiality_classification] || 0) + 1;
    return acc;
  }, {});

  // ESRS Category mapping
  const esrsCategoryNames = {
    'E1': 'E1 - Climate Change',
    'E2': 'E2 - Pollution',
    'E3': 'E3 - Water & Marine',
    'E4': 'E4 - Biodiversity',
    'E5': 'E5 - Circular Economy',
    'S1': 'S1 - Own Workforce',
    'S2': 'S2 - Value Chain Workers',
    'S3': 'S3 - Communities',
    'S4': 'S4 - Consumers',
    'G1': 'G1 - Business Conduct'
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-purple-800 rounded-lg p-8 text-white">
        <div className="flex items-center gap-3 mb-2">
          <Target size={36} />
          <h1 className="text-3xl font-bold">Double Materiality Assessment</h1>
        </div>
        <p className="text-purple-100">CSRD/ESRS Compliant - Impact & Financial Materiality Analysis</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-red-500">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-600">Double Material</h3>
            <CheckCircle2 className="text-red-600" size={24} />
          </div>
          <div className="text-3xl font-bold text-slate-900">
            {classificationCounts['Double Material'] || 0}
          </div>
          <p className="text-xs text-slate-500 mt-2">High impact & financial significance</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-orange-500">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-600">Impact Material</h3>
            <TrendingUp className="text-orange-600" size={24} />
          </div>
          <div className="text-3xl font-bold text-slate-900">
            {classificationCounts['Impact Material'] || 0}
          </div>
          <p className="text-xs text-slate-500 mt-2">Significant societal/environmental impact</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-blue-500">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-600">Financial Material</h3>
            <DollarSign className="text-blue-600" size={24} />
          </div>
          <div className="text-3xl font-bold text-slate-900">
            {classificationCounts['Financial Material'] || 0}
          </div>
          <p className="text-xs text-slate-500 mt-2">Material financial risk/opportunity</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-slate-400">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-600">Total Assessed</h3>
            <AlertCircle className="text-slate-600" size={24} />
          </div>
          <div className="text-3xl font-bold text-slate-900">
            {materialityData.length}
          </div>
          <p className="text-xs text-slate-500 mt-2">ESG topics evaluated</p>
        </div>
      </div>

      {/* Double Materiality Matrix */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold text-slate-900">Double Materiality Matrix</h2>
            <p className="text-sm text-slate-600">Impact Materiality vs. Financial Materiality</p>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-slate-700">Filter by ESRS:</label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-3 py-2 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              {categories.map(cat => (
                <option key={cat} value={cat}>
                  {cat === 'all' ? 'All Categories' : esrsCategoryNames[cat] || cat}
                </option>
              ))}
            </select>
          </div>
        </div>

        <ResponsiveContainer width="100%" height={500}>
          <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              type="number" 
              dataKey="x" 
              name="Impact Score" 
              domain={[0, 100]}
              label={{ value: 'Impact Materiality →', position: 'insideBottom', offset: -10 }}
            />
            <YAxis 
              type="number" 
              dataKey="y" 
              name="Financial Score" 
              domain={[0, 100]}
              label={{ value: '← Financial Materiality', angle: -90, position: 'insideLeft' }}
            />
            <ZAxis type="number" dataKey="size" range={[200, 500]} />
            <Tooltip 
              cursor={{ strokeDasharray: '3 3' }}
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  const data = payload[0].payload;
                  return (
                    <div className="bg-white p-3 border border-slate-300 rounded shadow-lg">
                      <p className="font-semibold text-slate-900">{data.name}</p>
                      <p className="text-sm text-slate-600">Category: {esrsCategoryNames[data.category]}</p>
                      <p className="text-sm text-blue-600">Impact: {data.x.toFixed(1)}</p>
                      <p className="text-sm text-green-600">Financial: {data.y.toFixed(1)}</p>
                      <p className="text-sm font-medium text-purple-600 mt-1">{data.classification}</p>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Scatter name="ESG Topics" data={filteredData} shape="circle">
              {filteredData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={getClassificationColor(entry.classification)} />
              ))}
            </Scatter>
            
            {/* Reference lines for materiality threshold */}
            <line x1="50" y1="0" x2="50" y2="100" stroke="#94a3b8" strokeDasharray="5 5" strokeWidth={2} />
            <line x1="0" y1="50" x2="100" y2="50" stroke="#94a3b8" strokeDasharray="5 5" strokeWidth={2} />
          </ScatterChart>
        </ResponsiveContainer>

        <div className="mt-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
          <div className="flex items-start gap-2">
            <Info className="text-purple-600 mt-0.5 flex-shrink-0" size={18} />
            <div className="text-xs text-purple-900">
              <p className="font-semibold mb-1">ESRS Double Materiality Approach:</p>
              <p>
                <span className="font-medium">Impact Materiality (Inside-Out):</span> Effects of the organization on people and environment. 
                <span className="font-medium ml-3">Financial Materiality (Outside-In):</span> Financial risks and opportunities from ESG factors.
                Topics scoring high on both dimensions are "Double Material" and require full ESRS disclosure.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-6 border-b border-slate-200">
          <h2 className="text-xl font-bold text-slate-900">Materiality Assessment Details</h2>
          <p className="text-sm text-slate-600">Detailed scoring for all evaluated ESG topics</p>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Topic</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">ESRS Category</th>
                <th className="px-6 py-3 text-center text-xs font-semibold text-slate-600 uppercase">Impact Score</th>
                <th className="px-6 py-3 text-center text-xs font-semibold text-slate-600 uppercase">Financial Score</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Classification</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Assessed By</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {materialityData
                .filter(item => selectedCategory === 'all' || item.category === selectedCategory)
                .map((item, idx) => (
                <tr key={idx} className="hover:bg-slate-50">
                  <td className="px-6 py-4 text-sm font-medium text-slate-900">{item.indicator_name}</td>
                  <td className="px-6 py-4 text-sm text-slate-600">
                    <span className="px-2 py-1 bg-slate-100 rounded text-xs font-medium">
                      {esrsCategoryNames[item.category] || item.category}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className="text-sm font-semibold text-blue-600">{item.impact_score.toFixed(1)}</span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className="text-sm font-semibold text-green-600">{item.financial_score.toFixed(1)}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span 
                      className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium"
                      style={{
                        backgroundColor: `${getClassificationColor(item.double_materiality_classification)}20`,
                        color: getClassificationColor(item.double_materiality_classification)
                      }}
                    >
                      {item.double_materiality_classification}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600">{item.assessed_by}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DoubleMateriality;
